import PauseIcon from '@mui/icons-material/Pause';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import { Box, Button, DialogActions, DialogContent, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { holdFromBillingTreatment } from 'src/config';
import { useFeatureToggleWhenReady } from 'src/hooks/use-feature-toggle';
import { Permissions, usePermission } from 'src/rbac';
import { holdDailyNoteFromBillingAction } from 'src/redux/actions/documentationAction';
import { BootstrapDialog } from '../common-modals/BootstrapDialog';
import ClosedPeriodDialog from '../dailySessionNotes/ClosedPeriodDialog';
import useClosedMonths from './use-closed-months';
import { isArchivedNote } from './archive-note';
import { formatDateOfService } from './format-date-of-service';
import { holdTestId } from './test-ids';

const isDailySessionNote = (document: any) =>
  Boolean(document?.dailySessionNoteUuid) &&
  Boolean(document?.formType?.includes('DAILY_SOAP_NOTE'));

export type HoldOrigin = 'MANUAL' | 'SYSTEM';

// THIN-8050 (epic THIN-8052, decision #10): "EOC close", never "discharge" -- the same noun as the
// Close-EOC question (THIN-8061). Working copy until the design pass settles the final wording.
export const MANUAL_HOLD_BADGE_LABEL = 'Held from billing';
export const SYSTEM_HOLD_BADGE_LABEL = 'Held \u00b7 planned EOC close';

/**
 * THIN-8050: a hold the platform applied on a planned EOC close (THIN-8047) gets its own badge, visibly
 * distinct from one a person applied, on both surfaces. Kerry, 11 Sept: a distinct badge, not a
 * tooltip. Releasing either works exactly the same way, so only the badge differs.
 */
export const HeldFromBillingBadge = ({ origin }: { origin?: HoldOrigin | null }) => {
  const isSystem = origin === 'SYSTEM';
  return (
    <Box
      data-testid={holdTestId('badge')}
      data-hold-origin={isSystem ? 'SYSTEM' : 'MANUAL'}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '2px',
        marginLeft: 1,
        padding: '4px 10px 4px 6px',
        borderRadius: '100px',
        backgroundColor: isSystem ? 'info.light' : 'warning.light',
        color: isSystem ? 'info.dark' : 'warning.dark',
        fontSize: '12px',
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      {isSystem ? (
        <PauseCircleIcon sx={{ fontSize: '16px', color: 'info.dark' }} />
      ) : (
        <PauseIcon sx={{ fontSize: '16px', color: 'warning.dark' }} />
      )}
      {isSystem ? SYSTEM_HOLD_BADGE_LABEL : MANUAL_HOLD_BADGE_LABEL}
    </Box>
  );
};

type HoldFromBillingDialogProps = {
  document: any;
  close: () => void;
  confirm: () => void;
};

const HoldFromBillingDialog = (props: HoldFromBillingDialogProps) => {
  const { document, close, confirm } = props;
  const patientData = useSelector((state: any) => state?.getSinglePatientReducer?.patient);

  const patientName =
    patientData?.lastName && patientData?.firstName
      ? `${patientData.lastName}, ${patientData.firstName}`
      : patientData?.name || '';
  const dateOfService = formatDateOfService(document?.dateOfService, 'MM/dd/yyyy');

  return (
    <BootstrapDialog
      PaperProps={{ style: { maxWidth: '750px' } }}
      data-testid={holdTestId('dialog')}
      onClose={close}
      open={Boolean(document)}
    >
      <Box p={3}>
        <DialogContent>
          <Box sx={{ display: 'flex' }}>
            <Box p={1}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  height: '100%',
                  marginRight: '12px',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: 'warning.light',
                  }}
                >
                  <PauseIcon sx={{ color: 'warning.dark' }} />
                </Box>
              </Box>
            </Box>
            <Box>
              <Typography variant="h5">Hold this note from billing?</Typography>
              <Typography
                variant="subtitle2"
                mt={1}
              >
                {[patientName, dateOfService].filter(Boolean).join(' · ')}
              </Typography>
              <Typography
                sx={{ color: 'text.secondary', fontWeight: '400', width: '100%' }}
                variant="body2"
                mt={1}
              >
                This note will be excluded from the billing export and UB04 report and the invoice,
                if this facility is invoiced.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            autoFocus
            data-testid={holdTestId('dialog-cancel')}
            sx={{ color: 'text.primary' }}
            onClick={close}
          >
            Cancel
          </Button>
          <Button
            autoFocus
            variant="contained"
            color="warning"
            data-testid={holdTestId('dialog-confirm')}
            sx={{
              backgroundColor: 'warning.dark',
              '&:hover': {
                backgroundColor: 'warning.dark',
              },
            }}
            onClick={confirm}
          >
            Hold from billing
          </Button>
        </DialogActions>
      </Box>
    </BootstrapDialog>
  );
};

export const useHoldFromBilling = () => {
  const dispatch = useDispatch();
  const [documentPendingHold, setDocumentPendingHold] = useState<any>(null);
  //  THIN-8138: the closed-period refusal, for callers that offer the action and then explain
  //  rather than hiding it. Owned here beside `holdConfirmationDialog` so every surface refuses
  //  in the same words instead of each growing its own dialog and state.
  const [isClosedPeriodRefusalOpen, setIsClosedPeriodRefusalOpen] = useState(false);
  const [isHoldFromBillingEnabled] = useFeatureToggleWhenReady(
    holdFromBillingTreatment,
    'ENTERPRISE'
  );
  const hasHoldFromBillingPermission = usePermission(Permissions.HOLD_DAILY_NOTE_FROM_BILLING);
  const canManageHoldFromBilling =
    isHoldFromBillingEnabled === true && hasHoldFromBillingPermission;
  const { isDateInClosedPeriod } = useClosedMonths(canManageHoldFromBilling);

  // A note whose date of service falls in a closed-and-signed period is locked for billing
  // until that period is re-opened — the same guardrail that already removes the edit action
  // (BOPS-108). Custom periods (BOPS-115) count, not just whole months.
  const isInClosedPeriod = useCallback(
    (document: any) => isDateInClosedPeriod(document?.dateOfService),
    [isDateInClosedPeriod]
  );

  useEffect(() => {
    if (!canManageHoldFromBilling || isInClosedPeriod(documentPendingHold)) {
      setDocumentPendingHold(null);
    }
  }, [canManageHoldFromBilling, documentPendingHold, isInClosedPeriod]);

  const setHold = (document: any, hold: boolean) => {
    if (!canManageHoldFromBilling || isInClosedPeriod(document)) return;
    dispatch(
      holdDailyNoteFromBillingAction({
        dailySessionNoteUuid: document?.dailySessionNoteUuid,
        hold,
      })
    );
  };

  const isHeld = (document: any) => document?.heldFromBilling === true;

  /**
   * Every rule except the closed-period one, shared by both directions. Split out so a caller can
   * tell the two refusals apart: "the hold can never apply to this note" and "it could apply once
   * the period is reopened" look identical to `canHold`/`canInclude` but are not the same thing
   * to the user.
   */
  const isHoldManageableIgnoringClosedPeriod = (document: any) =>
    canManageHoldFromBilling && isDailySessionNote(document) && !isArchivedNote(document);

  /**
   * THIN-8138: refuse-with-a-reason instead of doing nothing.
   *
   * Both entry points already returned early on a closed period, which is right — the hook stays
   * the authority and neither direction may run. What was missing is that the user was told
   * nothing. Returns whether the refusal was raised, so the caller stops there.
   */
  const refuseForClosedPeriod = (document: any) => {
    if (!isHoldManageableIgnoringClosedPeriod(document) || !isInClosedPeriod(document))
      return false;
    setIsClosedPeriodRefusalOpen(true);
    return true;
  };

  return {
    isHeldFromBilling: (document: any) => canManageHoldFromBilling && isHeld(document),
    /**
     * THIN-8050: who applied the hold, off the row's `holdOrigin` (THIN-8044). A held row that predates
     * the column, or one the reducer patched after a manual action, reads as MANUAL; an unheld row has
     * no origin at all.
     */
    holdOrigin: (document: any): HoldOrigin | null =>
      !isHeld(document) ? null : document?.holdOrigin === 'SYSTEM' ? 'SYSTEM' : 'MANUAL',
    //  BOPS-151/175: an archived note is out of billing for good and its DSN has already been
    //  deleted underneath, so neither direction of the hold can succeed on it. `canArchive`
    //  has always excluded an already-archived note; these two never did, which the Episode of
    //  Care hid by accident — `applyArchivedReadOnly` strips its whole menu — but the Daily
    //  Notes and Billing bar renders each control on its own predicate and showed it.
    canHold: (document: any) =>
      isHoldManageableIgnoringClosedPeriod(document) &&
      !isHeld(document) &&
      !isInClosedPeriod(document),
    requestHold: (document: any) => {
      if (refuseForClosedPeriod(document)) return;
      if (!canManageHoldFromBilling || isInClosedPeriod(document)) return;
      setDocumentPendingHold(document);
    },
    canInclude: (document: any) =>
      isHoldManageableIgnoringClosedPeriod(document) &&
      isHeld(document) &&
      !isInClosedPeriod(document),
    /**
     * Would be actionable but for the closed period — the action should be OFFERED and then
     * refuse with an explanation instead of disappearing. One predicate per direction so the
     * caller picks the same label it would have shown anyway.
     *
     * The Daily Notes and Billing page uses these because there the note is the whole screen, so a
     * control that is simply absent reads as a bug or a missing permission and leaves the user
     * nothing to click to find out why — the page already answers Edit and Start Note this way,
     * through the closed-period dialog.
     *
     * THIN-8138: the Episode of Care lists now use them too. They deliberately did not, on the
     * reasoning that an action hidden among others in a row menu is still fine. THIN-8063 removed
     * that option: on a planned EOC close into a closed period the hold is skipped and the user is
     * told to apply it by hand — and closing the EOC takes the patient off the Daily Notes and
     * Billing page, which leaves these menus the only surface that could offer it. Hidden there
     * meant being told to do something with nowhere to do it.
     */
    isHoldBlockedByClosedPeriod: (document: any) =>
      isHoldManageableIgnoringClosedPeriod(document) &&
      !isHeld(document) &&
      isInClosedPeriod(document),
    isIncludeBlockedByClosedPeriod: (document: any) =>
      isHoldManageableIgnoringClosedPeriod(document) &&
      isHeld(document) &&
      isInClosedPeriod(document),
    includeInBilling: (document: any) => {
      if (refuseForClosedPeriod(document)) return;
      setHold(document, false);
    },
    /**
     * THIN-8138: rendered by the surfaces that offer the action and then refuse — the Episode of
     * Care lists. The Daily Notes and Billing page routes its own refusal through the page-level
     * dialog it already shares with Edit and Start Note, so it does not render this one and the
     * two never both fire: that page tests `is…BlockedByClosedPeriod` before it ever calls in.
     */
    closedPeriodDialog: (
      <ClosedPeriodDialog
        open={isClosedPeriodRefusalOpen}
        onClose={() => setIsClosedPeriodRefusalOpen(false)}
      />
    ),
    holdConfirmationDialog: canManageHoldFromBilling ? (
      <HoldFromBillingDialog
        document={documentPendingHold}
        close={() => setDocumentPendingHold(null)}
        confirm={() => {
          setHold(documentPendingHold, true);
          setDocumentPendingHold(null);
        }}
      />
    ) : null,
  };
};
